import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as AddBookingButtonIcon } from '../../assets/icon/add-booking-room.svg';
import { fetchBookings } from '../../state/actions/booking';
import AddBookingModal from './AddBookingModal';
import moment from 'moment-timezone';
import './VirtualCalendar.css';

const SGT_TIMEZONE = 'Asia/Singapore';
const HOUR_HEIGHT = 60; // Height of each hour in pixels
const START_HOUR = 6; // Start at 6 AM
const END_HOUR = 24; // End at 12 AM (24 hours)
const TOTAL_HOURS = END_HOUR - START_HOUR;
const TOTAL_HEIGHT = TOTAL_HOURS * HOUR_HEIGHT;
const VIEWPORT_HEIGHT = 600; // Fixed viewport height

const VirtualCalendar = ({ selectedRooms = [], dateRange, search = '', selectedRoom = 'all', viewMode = 'daily', siteId, rooms: roomsFromProps = [] }) => {
  const dispatch = useDispatch();
  const { bookings, loading, activeTab } = useSelector((state) => ({
    bookings: state.booking?.bookings || [],
    loading: state.booking?.loading || false,
    activeTab: state.tab?.tab,
  }));


  const [rooms, setRooms] = useState([]);
  const [isBookingModalVisible, setIsBookingModalVisible] = useState(false);
  const [selectedBookingSlot, setSelectedBookingSlot] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);

  // Generate room colors
  const getRoomColor = (roomId) => {
    const colors = [
      '#8e44ad', // Purple
      '#e74c3c', // Red
      '#f39c12', // Orange
      '#27ae60', // Green
      '#3498db', // Blue
      '#e67e22', // Dark Orange
      '#9b59b6', // Light Purple
      '#1abc9c', // Teal
    ];
    const roomIndex = rooms.findIndex(r => r._id === roomId);
    return colors[roomIndex % colors.length];
  };

  // Generate lighter background colors
  const getRoomBackgroundColor = (roomId) => {
    const lightColors = [
      '#f4f1f8', // Light Purple
      '#fdf2f2', // Light Red
      '#fef9f0', // Light Orange
      '#f0f8f4', // Light Green
      '#f0f8ff', // Light Blue
      '#fef7f0', // Light Dark Orange
      '#f8f4f9', // Light Light Purple
      '#f0fdfc', // Light Teal
    ];
    const roomIndex = rooms.findIndex(r => r._id === roomId);
    return lightColors[roomIndex % lightColors.length];
  };

  // Calculate room availability (6am to 11:59pm = 18 hours)
  const calculateRoomStatus = (roomId) => {
    const roomBookings = bookings.filter(booking =>
      booking.roomIds.some(room => room._id === roomId)
    );

    if (roomBookings.length === 0) return 'Available';

    // Check if room is fully booked (6am-11:59pm)
    let totalBookedMinutes = 0;
    const totalAvailableMinutes = 18 * 60; // 6am to 11:59pm = 18 hours

    roomBookings.forEach(booking => {
      const start = moment(booking.startTimeSGT, 'YYYY-MM-DD HH:mm:ss');
      const end = moment(booking.endTimeSGT, 'YYYY-MM-DD HH:mm:ss');

      // Clamp to 6am-11:59pm range
      const startClamped = moment.max(start, moment(start).hour(6).minute(0));
      const endClamped = moment.min(end, moment(end).hour(23).minute(59));

      if (endClamped.isAfter(startClamped)) {
        totalBookedMinutes += endClamped.diff(startClamped, 'minutes');
      }
    });

    return totalBookedMinutes >= totalAvailableMinutes ? 'Full Booked' : 'Available';
  };

  // Update rooms when props change
  useEffect(() => {
    if (roomsFromProps && roomsFromProps.length > 0) {
      const formattedRooms = roomsFromProps.map(room => ({
        _id: room._id,
        name: room.name,
        capacity: room.capacity,
        availability: room.availability,
        image: room.image,
        status: calculateRoomStatus(room._id),
      }));
      setRooms(formattedRooms);
    }
  }, [roomsFromProps, bookings]);
  const [scrollTop, setScrollTop] = useState(0);
  const [currentTime, setCurrentTime] = useState(moment().tz(SGT_TIMEZONE));

  const scrollContainerRef = useRef();
  const eventPoolRef = useRef(new Map());

  // Calculate visible time range based on scroll position
  const visibleTimeRange = useMemo(() => {
    const startHourOffset = Math.floor(scrollTop / HOUR_HEIGHT);
    const endHourOffset = Math.ceil(
      (scrollTop + VIEWPORT_HEIGHT) / HOUR_HEIGHT
    );

    const startHour = Math.max(START_HOUR, START_HOUR + startHourOffset - 1);
    const endHour = Math.min(END_HOUR, START_HOUR + endHourOffset + 1);

    return { startHour, endHour };
  }, [scrollTop]);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment().tz(SGT_TIMEZONE));
    }, 60000);

    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    if (!dateRange?.start || !dateRange?.end) return;

    if (activeTab === 'dashboard') {
      dispatch(
        fetchBookings({
          startDate: dateRange.start,
          endDate: dateRange.end,
          search: search,
          roomId: selectedRooms,
          type: viewMode,
          siteId: siteId,
        })
      );
    }
  }, [ dateRange?.start, dateRange?.end, activeTab, search, selectedRooms, viewMode]);



  // Filter bookings for visible time range
  const visibleBookings = useMemo(() => {
    return bookings.filter((booking) => {
      // Use the SGT time directly from the API response
      const startTime = moment(booking.startTimeSGT, 'YYYY-MM-DD HH:mm:ss');
      const endTime = moment(booking.endTimeSGT, 'YYYY-MM-DD HH:mm:ss');
      const startHour = startTime.hour() + startTime.minute() / 60;
      const endHour = endTime.hour() + endTime.minute() / 60;

      return (
        startHour < visibleTimeRange.endHour &&
        endHour > visibleTimeRange.startHour
      );
    });
  }, [bookings, visibleTimeRange]);

  // Calculate position for booking events
  const getEventStyle = (booking, roomIndex) => {
    // Use the SGT time directly from the API response
    const startTime = moment(booking.startTimeSGT, 'YYYY-MM-DD HH:mm:ss');
    const endTime = moment(booking.endTimeSGT, 'YYYY-MM-DD HH:mm:ss');

    const startHour = startTime.hour() + startTime.minute() / 60;
    const endHour = endTime.hour() + endTime.minute() / 60;

    const top = (startHour - START_HOUR) * HOUR_HEIGHT;
    const height = Math.max((endHour - startHour) * HOUR_HEIGHT, 30);
    const roomWidth = `calc((100% - 80px) / ${Math.max(rooms.length, 1)})`;
    const left = `calc(80px + ${roomIndex} * ${roomWidth})`;
    const width = roomWidth;

    return {
      position: 'absolute',
      top: `${top}px`,
      height: `${height}px`,
      left,
      width,
      transform: 'translateZ(0)', // Enable hardware acceleration
    };
  };

  // Get current time indicator position
  const getCurrentTimePosition = () => {
    const hour = currentTime.hour() + currentTime.minute() / 60;
    if (hour < START_HOUR || hour > END_HOUR) return null;

    return (hour - START_HOUR) * HOUR_HEIGHT;
  };

  // Handle scroll events
  const handleScroll = useCallback((e) => {
    const newScrollTop = e.target.scrollTop;
    setScrollTop(newScrollTop);
  }, []);

  // Handle booking slot click
  const handleBookingSlotClick = (roomId, hour, date) => {
    setSelectedBookingSlot({
      roomId:[roomId],
      hour,
      date: date || dateRange?.start,
    });
    setIsBookingModalVisible(true);
  };

  // Handle modal close
  const handleBookingModalCancel = () => {
    setIsBookingModalVisible(false);
    setSelectedBookingSlot(null);
    setEditingBooking(null);
  };

  // Handle booking event click (edit mode)
  const handleBookingEventClick = (booking) => {
    setEditingBooking(booking);
    setSelectedBookingSlot(null);
    setIsBookingModalVisible(true);
  };

  // Handle booking creation success
  const handleBookingSubmit = () => {
    // Refresh bookings after creating new booking
    if (dateRange?.start && dateRange?.end && activeTab === 'dashboard') {
      dispatch(
        fetchBookings({
          startDate: dateRange.start,
          endDate: dateRange.end,
          search: search,
          roomId: selectedRooms,
          type: viewMode,
          siteId: siteId,
        })
      );
    }
  };

  // Generate time slots
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = START_HOUR; hour < END_HOUR; hour++) {
      const time = moment().hour(hour).minute(0);
      slots.push({
        hour,
        time: time.format('h:mm A'),
        position: (hour - START_HOUR) * HOUR_HEIGHT,
      });
    }
    return slots;
  }, []);

  const currentTimePosition = getCurrentTimePosition();

  return (
    <div className="virtual-calendar">
      {loading && <div className="calendar-loading">Loading...</div>}

      {/* Calendar Header */}
      <div className="calendar-header">
        <div className="time-column-header">UTC+08</div>
        {rooms.map((room) => (
          <div key={room._id} className="room-header">
            <div className="room-name">{room.name}</div>
            <div className="room-id">ID {room._id.slice(-6)}</div>
            <div
              className={`room-status status-${room.status
                .toLowerCase()
                .replace(' ', '-')}`}
            >
              {room.status}
            </div>
          </div>
        ))}
      </div>

      {/* Scrollable Calendar Body */}
      <div
        ref={scrollContainerRef}
        className="calendar-body"
        style={{ height: VIEWPORT_HEIGHT }}
        onScroll={handleScroll}
      >
        <div className="calendar-content" style={{ height: TOTAL_HEIGHT }}>
          {/* Time Grid Lines */}
          {timeSlots.map((slot) => (
            <div
              key={slot.hour}
              className="time-slot"
              style={{ top: slot.position }}
            >
              <div className="time-label">{slot.time}</div>
              <div className="time-line" />
            </div>
          ))}

          {/* Current Time Indicator */}
          {currentTimePosition !== null && (
            <div
              className="current-time-line"
              style={{ top: `${currentTimePosition}px` }}
            />
          )}

          {/* Room Columns */}
          {rooms.map((room, roomIndex) => {
            const roomWidth = `calc((100% - 80px) / ${rooms.length})`;
            const roomLeft = `calc(80px + ${roomIndex} * ${roomWidth})`;

            return (
              <div
                key={room._id}
                className="room-column"
                style={{
                  left: roomLeft,
                  width: roomWidth,
                  height: TOTAL_HEIGHT,
                }}
              >
                {/* Add Booking Buttons */}
                {timeSlots.map((slot) => (
                  <div
                    key={`${room._id}-${slot.hour}`}
                    className="add-booking-slot"
                    style={{ top: slot.position, height: HOUR_HEIGHT }}
                    onClick={() => handleBookingSlotClick(room._id, slot.hour, dateRange?.start)}
                  >
                    <AddBookingButtonIcon style={{ cursor: 'pointer' }}/>
                  </div>
                ))}
              </div>
            );
          })}

          {/* Booking Events */}
          {visibleBookings.map((booking) =>
            booking.roomIds?.map((roomAssignment) => {
              const roomIndex = rooms.findIndex(
                (r) => r._id === roomAssignment._id
              );
              if (roomIndex === -1) return null;

              const roomColor = getRoomColor(roomAssignment._id);
              const roomBackgroundColor = getRoomBackgroundColor(roomAssignment._id);
              const eventStyle = {
                ...getEventStyle(booking, roomIndex),
                borderLeftColor: roomColor,
                backgroundColor: roomBackgroundColor,
                cursor: 'pointer',
              };

              return (
                <div
                  key={`${booking._id}-${roomAssignment._id}`}
                  className="booking-event"
                  style={eventStyle}
                  onClick={() => handleBookingEventClick(booking)}
                >
                  <div className="booking-title">{booking.title}</div>
                  <div className="booking-person">
                    Booked By {booking.picName}
                  </div>
                  <div className="booking-time">
                    {moment(booking.startTimeSGT, 'YYYY-MM-DD HH:mm:ss').format(
                      'h:mm A'
                    )}{' '}
                    -{' '}
                    {moment(booking.endTimeSGT, 'YYYY-MM-DD HH:mm:ss').format(
                      'h:mm A'
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Add Booking Modal */}
      <AddBookingModal
        isVisible={isBookingModalVisible}
        onCancel={handleBookingModalCancel}
        onSubmit={handleBookingSubmit}
        selectedRoom={selectedBookingSlot?.roomId}
        selectedDate={selectedBookingSlot?.date}
        selectedHour={selectedBookingSlot?.hour}
        rooms={roomsFromProps}
        editingBooking={editingBooking}
      />
    </div>
  );
};

export default VirtualCalendar;
